import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchPublicPropertyById } from './publicPropertyApi';
import PublicHeader from './PublicHeader';
import './PropertyPublic.css';

function PropertyPublic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');
  const [activeImage, setActiveImage] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      setActiveImage(0);
      const { data, error } = await fetchPublicPropertyById(id);
      if (cancelled) return;
      if (error) {
        setLoadError(error.message || String(error));
        setProperty(null);
      } else {
        setProperty(data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="pp-root">
        <PublicHeader />
        <div className="pp-missing">
          <p className="pp-missing-text">Loading property…</p>
          <Link to="/" className="pp-back-link">
            ← Back to listings
          </Link>
        </div>
      </div>
    );
  }

  if (loadError || !property) {
    return (
      <div className="pp-root">
        <PublicHeader />
        <div className="pp-missing">
          <p className="pp-missing-text">
            {loadError || 'We couldn’t find that property.'}
          </p>
          <Link to="/" className="pp-back-link">
            ← Back to listings
          </Link>
        </div>
      </div>
    );
  }

  const images = property.gallery?.length ? property.gallery : [property.image];

  return (
    <div className="pp-root">
      <PublicHeader />

      <main className="pp-main">
        <div className="pp-gallery">
          <div className="pp-hero-wrap">
            <img src={images[activeImage]} alt="" className="pp-hero-img" />
            {property.tag && (
              <span
                className={`pp-badge pp-badge--${
                  property.tag === 'New' ? 'new' : 'feat'
                }`}
              >
                {property.tag}
              </span>
            )}
          </div>
          <ul className="pp-thumbs" aria-label="Gallery images">
            {images.map((src, i) => (
              <li key={src + String(i)}>
                <button
                  type="button"
                  className={`pp-thumb ${i === activeImage ? 'pp-thumb--on' : ''}`}
                  onClick={() => setActiveImage(i)}
                  aria-label={`Show image ${i + 1}`}
                  aria-pressed={i === activeImage}
                >
                  <img src={src} alt="" />
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="pp-content">
          <p className="pp-loc">{property.location}</p>
          <h1 className="pp-title">{property.title}</h1>

          <ul className="pp-meta">
            <li>
              <span className="pp-meta-val">{property.beds}</span>
              <span className="pp-meta-label">Beds</span>
            </li>
            <li>
              <span className="pp-meta-val">
                {property.baths != null ? property.baths : '—'}
              </span>
              <span className="pp-meta-label">Baths</span>
            </li>
            <li>
              <span className="pp-meta-val">
                {property.sqft != null ? property.sqft : '—'}
              </span>
              <span className="pp-meta-label">Sq ft</span>
            </li>
          </ul>

          <div className="pp-price-row">
            <div>
              <span className="pp-price">£{property.rent.toLocaleString()}</span>
              <span className="pp-price-unit"> / month</span>
            </div>
            <span className="pp-available">{property.availableFrom}</span>
          </div>

          <section className="pp-section">
            <h2 className="pp-section-title">About this home</h2>
            <p className="pp-desc">{property.description}</p>
          </section>

          <section className="pp-section">
            <h2 className="pp-section-title">Highlights</h2>
            <ul className="pp-tags">
              {(property.highlights || []).map((h) => (
                <li key={h} className="pp-tag">
                  {h}
                </li>
              ))}
            </ul>
          </section>

          <div className="pp-cta" id="enquire">
            <button
              type="button"
              className="pp-btn pp-btn-primary pp-btn-block"
              onClick={() => navigate(`/property/${property.id}/enquiry`)}
            >
              Enquire about this property
            </button>
            <p className="pp-cta-note">
              Typical response within 24 hours on business days.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

export default PropertyPublic;
